import { ReactNode } from "react";
import { Outlet, RouteObject } from "react-router-dom";
import { OidcGuard } from "../components/internal/OidcGuard";
import { OidcProvider } from "../components/internal/OidcProvider";
import { OidcAuthenticating } from "../components/internal/OidcStateComponents";
import { OidcAppConfiguration, OidcContext } from "../types/oidcTypes";
import { getIdpConfiguration, getOidcAppConfiguration } from "./oidcConfiguration";

export interface CreateOidcAuthorizedRoutesProps {
  oidcContext: OidcContext;

  errorComponent?: (configuration: OidcAppConfiguration) => ReactNode;
  loginComponent?: (configuration: OidcAppConfiguration) => ReactNode;
  logoutComponent?: (configuration: OidcAppConfiguration) => ReactNode;

  unguardedRoutes?: RouteObject[];
  guardedRoutes?: RouteObject[];
}

export const createOidcAuthorizedRoutes = (
  {
    oidcContext,
    errorComponent,
    loginComponent,
    logoutComponent,
    unguardedRoutes,
    guardedRoutes,
  }: CreateOidcAuthorizedRoutesProps,
): RouteObject[] => {
  const idpConfiguration = getIdpConfiguration(oidcContext);
  const oidcAppConfiguration = getOidcAppConfiguration(oidcContext);

  const LoginComponent = loginComponent?.(oidcAppConfiguration);
  const LogoutComponent = logoutComponent?.(oidcAppConfiguration);
  const errorElement = errorComponent?.(oidcAppConfiguration);

  return [
    {
      element: (
        <OidcProvider idpConfiguration={idpConfiguration} oidcAppConfiguration={oidcAppConfiguration}>
          <Outlet/>
        </OidcProvider>
      ),
      errorElement,
      children: [
        {
          children: [
            {
              path: oidcAppConfiguration.pathAuthorize,
              element: <OidcAuthenticating/>,
            },
            {
              path: oidcAppConfiguration.pathLogin,
              element: LoginComponent,
            },
            {
              path: oidcAppConfiguration.pathLogout,
              element: LogoutComponent,
            },
          ],
        },
        {
          children: unguardedRoutes ?? [],
          errorElement,
        },
        {
          element: (
            <OidcGuard idpConfiguration={idpConfiguration} oidcAppConfiguration={oidcAppConfiguration}>
              <Outlet/>
            </OidcGuard>
          ),
          children: guardedRoutes ?? [],
          errorElement,
        },
      ],
    },
  ];
};
